import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { createCsvStatsHook } from '../lib/hookFactories';
import {
  ImperiledSpeciesClass,
  ImperiledSpeciesClassFraction,
  ImperiledSpeciesStats,
} from '../lib/types';
import { IMPERILED_SPECIES_PALETTE } from '../lib/imperiledSpeciesColors';

// CSV schema: class,label,fraction_of_shed,acres
// One row per 5 MoBI classes plus one `background` row covering zero-value
// (no-imperiled-species-signal) pixels. `fraction_of_shed` is the share of
// the full supply shed; the UI derives class-of-signal fractions from the
// per-class acres so the bar chart answers "given there's signal, how is it
// distributed?" — matching the WHP framing of class-of-burnable.
const VALID_CLASSES = new Set<ImperiledSpeciesClass>(IMPERILED_SPECIES_PALETTE.order);

const parseImperiledSpeciesStats = (text: string): ImperiledSpeciesStats => {
  const rows = parseCsv(text);
  const classAcres = new Map<ImperiledSpeciesClass, number>();
  let backgroundAcres = 0;
  let totalAcres = 0;

  rows.forEach(row => {
    const acres = Number(row['acres']);
    if (!Number.isFinite(acres)) return;
    totalAcres += acres;

    const cls = row['class'];
    if (cls === 'background') {
      backgroundAcres = acres;
    } else if (VALID_CLASSES.has(cls as ImperiledSpeciesClass)) {
      classAcres.set(cls as ImperiledSpeciesClass, acres);
    }
  });

  const signalAcres = Math.max(totalAcres - backgroundAcres, 0);
  const classes: ImperiledSpeciesClassFraction[] = IMPERILED_SPECIES_PALETTE.order.map(cls => {
    const acres = classAcres.get(cls) ?? 0;
    return {
      class: cls,
      signalAcres: acres,
      fraction: signalAcres > 0 ? acres / signalAcres : 0,
    };
  });

  return {
    classes,
    backgroundFraction: totalAcres > 0 ? backgroundAcres / totalAcres : 0,
    signalFraction: totalAcres > 0 ? signalAcres / totalAcres : 0,
    totalAcres,
  };
};

/**
 * Fetches MoBI PWRSR class-fraction stats for the supply shed defined by
 * (millCode, driveTime). CSV lives at sfi/mobi/{mill}_{driveTime}min_mobi.csv.
 */
export const useImperiledSpecies = createCsvStatsHook({
  path: API_CONFIG.s3.paths.mobiStats,
  parse: parseImperiledSpeciesStats,
  errorPrefix: 'Failed to load imperiled species data',
});
